import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { FormikErrors } from "formik";

import FormField from "../form-field/FormField";
import { DynamicProperty } from "./DynamicProperty";
import { SubSection, DataSection } from "./sections/DataSections";
import DatePicker from "../datepicker/DatePicker";
import TimePicker from "../timepicker/TimePicker";
import { Time } from "../timepicker/Time";
import { dateService } from "../../services/date-service/DateService";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import {
  DynamicPropertiesOrganizer,
  PropertiesGrouped,
  PropertiesInColumn,
} from "./DynamicPropertiesOrganizer";
import "./DynamicProperties.scss";
import { RequiredValidatorModel } from "../../services/form/validators/required/RequiredValidator";
import { DecimalNumberValidatorModel } from "../../services/form/validators/number/DecimalNumberValidator";
import { IntegerNumberValidatorModel } from "../../services/form/validators/number/IntegerNumberValidator";
import { translationService } from "../../services/translation/TranslationService";
import { Dropdown, DropdownOption } from "../dropdown/Dropdown";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { FieldsFromBM } from "../../api-models/response/BluemetricFields";

interface EditDynamicPropertiesInSectionsProps {
  properties: DynamicProperty[];
  labelKey?: string;
  values: FieldsFromBM;
  setFieldValue?: (field: string, value: any) => Promise<void | FormikErrors<any>>;
  allFieldsDisabled?: boolean;
  loadingOverlay?: boolean;
  nestedFieldsPath?: string;
}
interface EditDynamicPropertyProps {
  id: string;
  property: DynamicProperty;
  value: any;
  setFieldValue?: (field: string, value: any) => Promise<void | FormikErrors<any>>;
  disabled?: boolean;
  nestedFieldPath?: string;
}

interface EditDynamicDatePropertyProps extends EditDynamicPropertyProps {
  onlyYear?: boolean;
}

interface EditDynamicPropertiesInSectionProps {
  labelKey?: string;
  values: FieldsFromBM;
  setFieldValue?: (field: string, value: any) => Promise<void | FormikErrors<any>>;
  allFieldsDisabled?: boolean;
  loadingOverlay?: boolean;
  nestedFieldsPath?: string;
  propertyGrouped: PropertiesGrouped;
}

export const EditDynamicProperties = observer((props: EditDynamicPropertiesInSectionsProps) => {
  const {
    properties,
    labelKey,
    values,
    setFieldValue,
    allFieldsDisabled,
    loadingOverlay,
    nestedFieldsPath,
  } = props;
  const propertiesObjs = properties.map((property) => new DynamicProperty(property));

  return (
    <div>
      {loadingOverlay && <LoadingSpinner overlay />}
      <EditDynamicPropertiesInSections
        labelKey={labelKey}
        properties={propertiesObjs}
        values={values}
        setFieldValue={setFieldValue}
        allFieldsDisabled={allFieldsDisabled}
        nestedFieldsPath={nestedFieldsPath}
      />
    </div>
  );
});

const EditDynamicPropertiesInSections = observer((props: EditDynamicPropertiesInSectionsProps) => {
  const { properties, values, setFieldValue, nestedFieldsPath, allFieldsDisabled } = props;
  const dynamicPropertiesOrganizer = new DynamicPropertiesOrganizer(properties);
  const propertiesGrouped = dynamicPropertiesOrganizer.getGroupedProperties();

  return (
    <div>
      {propertiesGrouped.map((propertyGrouped: PropertiesGrouped, index) => {
        return (
          <EditDynamicPropertiesInSection
            key={index}
            values={values}
            propertyGrouped={propertyGrouped}
            labelKey={propertyGrouped.caption}
            nestedFieldsPath={nestedFieldsPath}
            setFieldValue={setFieldValue}
            allFieldsDisabled={allFieldsDisabled}
          />
        );
      })}
    </div>
  );
});

const EditDynamicPropertiesInSection = observer((props: EditDynamicPropertiesInSectionProps) => {
  const { propertyGrouped, values, setFieldValue, allFieldsDisabled, nestedFieldsPath, labelKey } =
    props;

  return (
    <div className="section">
      <DataSection
        labelKey={propertyGrouped.caption ? undefined : labelKey}
        label={propertyGrouped.caption}
      >
        {propertyGrouped
          .getPropertiesGroupedInColumns()
          .map((propertiesInColumns: PropertiesInColumn, index) => {
            return (
              <EditDynamicPropertiesInColumns
                key={index}
                properties={propertiesInColumns.properties}
                values={values}
                setFieldValue={setFieldValue}
                allFieldsDisabled={allFieldsDisabled}
                nestedFieldsPath={nestedFieldsPath}
              />
            );
          })}
      </DataSection>

      {propertyGrouped.getLongTextProperties().map((property: DynamicProperty) => {
        return (
          <EditDynamicTextProperty
            key={property.metadata.indicator}
            id={property.metadata.indicator}
            property={property}
            value={values[property.metadata.indicator] || ""}
            disabled={allFieldsDisabled}
            nestedFieldPath={nestedFieldsPath}
          />
        );
      })}
      <hr></hr>
    </div>
  );
});

const EditDynamicPropertiesInColumns = observer((props: EditDynamicPropertiesInSectionsProps) => {
  const { properties, values, setFieldValue, allFieldsDisabled, nestedFieldsPath } = props;

  return (
    <SubSection>
      {properties.map((property: DynamicProperty) => {
        if (property.isString()) {
          return (
            <EditDynamicStringProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator] || ""}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isNumericInteger()) {
          return (
            <EditDynamicNumericIntegerInputProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator] || ""}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isNumericDecimal()) {
          return (
            <EditDynamicNumericDecimalInputProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator] || ""}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isDropdown()) {
          return (
            <EditDynamicDropdownProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator] || ""}
              setFieldValue={setFieldValue}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isDate()) {
          return (
            <EditDynamicDateProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator]}
              setFieldValue={props.setFieldValue}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
              onlyYear={property.isOfTypeYear()}
            />
          );
        }
        if (property.isBoolean()) {
          return (
            <EditDynamicBooleanProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator]}
              setFieldValue={setFieldValue}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isTime()) {
          return (
            <EditDynamicTimeProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator]}
              setFieldValue={setFieldValue}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        if (property.isVideoPositionTime()) {
          return (
            <EditVideoPositionTimeProperty
              key={property.metadata.indicator}
              id={property.metadata.indicator}
              property={property}
              value={values[property.metadata.indicator]}
              setFieldValue={setFieldValue}
              disabled={allFieldsDisabled}
              nestedFieldPath={nestedFieldsPath}
            />
          );
        }
        return null;
      })}
    </SubSection>
  );
});

const EditDynamicStringProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, disabled, nestedFieldPath } = props;
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));
  const propertyRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="input"
      className="input"
      labelKey={property.getLabelText()}
      required={propertyRequired}
      component="input"
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={value}
      validator={requiredValidator}
      maxLength={property.metadata.length}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      disabled={disabled || !isEditable}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
      placeholderKey="general.add-value"
    />
  );
});

const EditDynamicNumericIntegerInputProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, disabled, nestedFieldPath } = props;
  const isRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  const [requiredIntegerNumberValidator] = useState(() =>
    IntegerNumberValidatorModel.create({
      length: property.metadata.length,
      maxValue: property.metadata.maxValue ? Number(property.metadata.maxValue) : null,
      isRequired: isRequired,
      format: property.metadata.formatString,
    })
  );

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="input"
      className="input"
      labelKey={property.getLabelText()}
      required={isRequired}
      component="input"
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={value}
      validator={requiredIntegerNumberValidator}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      disabled={disabled || !isEditable}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
      placeholderKey="general.add-value"
    />
  );
});

const EditDynamicNumericDecimalInputProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, disabled, nestedFieldPath } = props;
  const isRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  const [requiredDecimalNumberValidator] = useState(() =>
    DecimalNumberValidatorModel.create(
      {
        length: property.metadata.length,
        maxValue: property.metadata.maxValue ? Number(property.metadata.maxValue) : null,
        precision: property.metadata.precision,
        isRequired: isRequired,
      },
      {
        translationService,
      }
    )
  );

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="input"
      className="input"
      labelKey={property.getLabelText()}
      required={isRequired}
      component="input"
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={value}
      validator={requiredDecimalNumberValidator}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      disabled={disabled || !isEditable}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
      placeholderKey="general.add-value"
    />
  );
});

const EditDynamicDropdownProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, setFieldValue, disabled, nestedFieldPath } = props;
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const propertyRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  const optionsList: DropdownOption[] = useMemo(() => {
    if (!property.metadata.catalog) return [];

    return property.metadata.catalog.map((option) => {
      return {
        id: option.key,
        name: option.value,
      };
    });
  }, [property.metadata.catalog]);

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="select"
      className="select"
      labelKey={property.getLabelText()}
      required={propertyRequired}
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={value}
      validator={requiredValidator}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      disabled={disabled || !isEditable}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
      children={() => (
        <Dropdown
          placeholderKey="general.add-value"
          disabled={disabled || !isEditable}
          selectedOption={optionsList.find((o) => o.id === value) ?? { id: "", name: "" }}
          options={optionsList}
          fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
          canClearSelection={!propertyRequired}
          setFieldValue={(field: string, value: any) => {
            setFieldValue?.(field, value?.id ?? "");
          }}
        />
      )}
    />
  );
});

const EditDynamicDateProperty = observer((props: EditDynamicDatePropertyProps) => {
  const { property, value, setFieldValue, disabled, nestedFieldPath, onlyYear } = props;
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const propertyRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);
  const formatString = useMemo(
    () => property.metadata.formatString,
    [property.metadata.formatString]
  );

  const dateValue = useMemo(() => {
    if (!value) return null;
    if (formatString && typeof value === "string") {
      return dateService.getDateBasedOnBMFormat(value, formatString);
    } else {
      return new Date(value);
    }
  }, [formatString, value]);

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="input"
      labelKey={property.getLabelText()}
      required={propertyRequired}
      fieldName={fieldName}
      fieldValue={dateValue}
      validator={requiredValidator}
      component={() => (
        <DatePicker
          isRequired={propertyRequired}
          value={dateValue}
          onlyYear={onlyYear}
          onChange={(date) =>
            setFieldValue
              ? setFieldValue(
                  nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName,
                  date ? dateService.dateToUTC(date) : null
                )
              : undefined
          }
          placeholderKey="general.add-value"
        />
      )}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      disabled={disabled || !isEditable}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
    />
  );
});

const EditDynamicTimeProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, setFieldValue, disabled, nestedFieldPath } = props;
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const propertyRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  const time = useMemo((): Time => {
    const time = (value as string).split(":");
    return new Time(parseInt(time[0]), parseInt(time[1]), parseInt(time[2]));
  }, [value]);

  const updateTime = useCallback(
    (time: Time) => {
      if (setFieldValue) {
        setFieldValue(
          nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName,
          time.format()
        );
      }
    },
    [fieldName, nestedFieldPath, setFieldValue]
  );

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="input"
      labelKey={property.getLabelText()}
      required={propertyRequired}
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={time}
      validator={requiredValidator}
      component={() => (
        <TimePicker value={time} onChange={updateTime} disabled={disabled || !isEditable} />
      )}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
      placeholderKey="general.add-value"
    />
  );
});

const EditVideoPositionTimeProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, setFieldValue, disabled, nestedFieldPath } = props;
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const propertyRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  const timeForComputation = useMemo(() => {
    return value
      ? value
      : (videoPlayerService.selectedVADetection.timestamp ?? 0) >= 0
      ? videoPlayerService.selectedVADetection.timestamp
      : null;
  }, [value]);

  useEffect(() => {
    if (setFieldValue) {
      setFieldValue(
        nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName,
        timeForComputation
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const time = useMemo((): Time => {
    if (timeForComputation) {
      const minutes = Math.floor(parseInt(timeForComputation) / 60);
      const seconds = parseInt(timeForComputation) % 60;
      return new Time(0, minutes, seconds);
    }
    return new Time(0, 0, 0);
  }, [timeForComputation]);

  const updateVideoPositionTime = useCallback(
    (time: Time) => {
      if (setFieldValue) {
        setFieldValue(
          nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName,
          time.totalSeconds()
        );
      }
    },
    [fieldName, nestedFieldPath, setFieldValue]
  );

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="input"
      labelKey={property.getLabelText()}
      required={propertyRequired}
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={timeForComputation}
      validator={requiredValidator}
      disabled={disabled}
      component={() => (
        <TimePicker
          value={time}
          onChange={updateVideoPositionTime}
          withHour={false}
          disabled={disabled || !isEditable}
          showDefaultTime={timeForComputation || timeForComputation === 0}
        />
      )}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
    />
  );
});

const EditDynamicBooleanProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, setFieldValue, disabled, nestedFieldPath } = props;
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const propertyRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  const getOptionsList = useCallback(() => {
    return [
      {
        id: "1",
        name: translationService.translate("general.yes"),
      },
      {
        id: "0",
        name: translationService.translate("general.no"),
      },
    ];
  }, []);

  return (
    <FormField
      key={fieldName}
      id={fieldName}
      fieldType="select"
      className="select"
      labelKey={property.getLabelText()}
      required={propertyRequired}
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={value}
      validator={requiredValidator}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      disabled={disabled || !isEditable}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
      children={() => (
        <Dropdown
          disabled={disabled || !isEditable}
          selectedOption={getOptionsList().find((o) => o.id === value) ?? { id: "", name: "" }}
          options={getOptionsList()}
          fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
          canClearSelection={!propertyRequired}
          setFieldValue={(field: string, value: any) => {
            setFieldValue?.(field, value?.id ?? "");
          }}
          placeholderKey="general.add-value"
        />
      )}
    />
  );
});

const EditDynamicTextProperty = observer((props: EditDynamicPropertyProps) => {
  const { property, value, disabled, nestedFieldPath } = props;
  const [requiredValidator] = useState(() => RequiredValidatorModel.create({}));

  const propertyRequired: boolean = useMemo(() => property.isRequired(), [property]);
  const isEditable = useMemo(() => property.isEditable(), [property]);
  const fieldName = useMemo(() => property.metadata.indicator, [property.metadata.indicator]);

  return (
    <FormField
      id={fieldName}
      fieldType="textarea"
      className="textarea"
      labelKey={property.getLabelText()}
      required={propertyRequired}
      component="textarea"
      fieldName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      fieldValue={value}
      validator={requiredValidator}
      maxLength={property.metadata.length}
      errorName={nestedFieldPath ? `${nestedFieldPath}.${fieldName}` : fieldName}
      disabled={disabled || !isEditable}
      tooltip={property.metadata.description}
      labelIcon={property.getIssueIcon()}
      placeholderKey="general.add-value"
    />
  );
});
